import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, ExternalLink, X } from "lucide-react";

import { fetchDocumentBytes, openDocument } from "../utils/documentFiles";
import { pdfjsLib } from "../utils/pdfjs";

/**
 * Quick in-app snapshot view of a document. PDFs render page by page with
 * pdfjs; images render directly. No new tab, no handing off to Adobe —
 * the person stays in PME People and can still download or pop the file out
 * if they need the full viewer.
 *
 * `docked` shows it as a panel down the right of a wide screen (full screen
 * on a phone) so the person can read it while they fill in a form beside it.
 */
export default function DocumentPreviewModal({
  documentId,
  title,
  fileType,
  onClose,
  docked = false,
}: {
  documentId: number;
  title: string;
  fileType: string;
  onClose: () => void;
  docked?: boolean;
}) {
  const { t } = useTranslation();
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isPdf = fileType.toLowerCase() === "pdf";

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const bytes = await fetchDocumentBytes(documentId);
        if (cancelled) return;
        if (isPdf) {
          const doc = await pdfjsLib.getDocument({ data: bytes }).promise;
          if (cancelled) return;
          setPdfDoc(doc);
        } else {
          objectUrl = URL.createObjectURL(new Blob([bytes]));
          setImageUrl(objectUrl);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId, isPdf]);

  return (
    <div
      className={
        docked
          ? "fixed inset-0 z-[70] flex bg-black/40 p-2 lg:inset-y-0 lg:left-auto lg:right-0 lg:w-[min(46vw,760px)] lg:bg-transparent lg:p-0"
          : "fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-3 sm:p-6"
      }
      onClick={onClose}
      role={docked ? "complementary" : "dialog"}
      aria-label={title}
    >
      <div
        className={
          docked
            ? "flex h-full w-full flex-col overflow-hidden rounded-xl bg-white shadow-xl lg:rounded-none lg:border-l lg:border-gray-200 lg:shadow-2xl"
            : "flex h-full w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        }
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2.5">
          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
            {title}
          </p>
          <button
            type="button"
            onClick={() => void openDocument(documentId, "attachment")}
            title={t("documents.download", "Download")}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <Download size={14} strokeWidth={2} aria-hidden />
            <span className="hidden sm:inline">{t("documents.download", "Download")}</span>
          </button>
          <button
            type="button"
            onClick={() => void openDocument(documentId, "inline")}
            title={t("documents.openInNewTab", "Open in new tab")}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <ExternalLink size={14} strokeWidth={2} aria-hidden />
            <span className="hidden sm:inline">{t("documents.openInNewTab", "Open in new tab")}</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close", "Close")}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
          >
            <X size={18} strokeWidth={2} aria-hidden />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-gray-100 p-3 sm:p-5">
          {loading && (
            <p className="py-10 text-center text-sm text-gray-500">
              {t("common.loading", "Loading…")}
            </p>
          )}
          {error && (
            <div className="py-10 text-center">
              <p className="text-sm text-red-600">
                {t("documents.previewFailed", "Couldn't open a preview of this file.")}
              </p>
              <button
                type="button"
                onClick={() => void openDocument(documentId, "inline")}
                className="mt-3 rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white"
              >
                {t("documents.openInNewTab", "Open in new tab")}
              </button>
            </div>
          )}
          {!loading && !error && isPdf && pdfDoc && (
            <div className="mx-auto max-w-2xl space-y-3">
              {Array.from({ length: pdfDoc.numPages }, (_, i) => (
                <PreviewPdfPage key={i + 1} pdfDoc={pdfDoc} pageNumber={i + 1} />
              ))}
            </div>
          )}
          {!loading && !error && !isPdf && imageUrl && (
            <div className="flex justify-center">
              <img
                src={imageUrl}
                alt={title}
                className="max-h-full max-w-full rounded-lg bg-white shadow-sm"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewPdfPage({
  pdfDoc,
  pageNumber,
}: {
  pdfDoc: pdfjsLib.PDFDocumentProxy;
  pageNumber: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const page = await pdfDoc.getPage(pageNumber);
      if (cancelled) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const containerWidth = canvas.parentElement?.clientWidth ?? 640;
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = Math.min(containerWidth / baseViewport.width, 2);
      const dpr = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: scale * dpr });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width / dpr}px`;
      canvas.style.height = `${viewport.height / dpr}px`;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      await page.render({ canvasContext: ctx, viewport, canvas }).promise;
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, pageNumber]);

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm">
      <canvas ref={canvasRef} className="block w-full" />
    </div>
  );
}
